/**
 * Firmaet som sin egen rad — Kontakter, andre halvdel (vedtak 100.3, migrering 0088).
 *
 * ONE DTO, NOT A SUMMARY AND A DETAIL. The row is nine short fields; a second shape whose only
 * difference is that it also carries `notes` would be two things to keep in step for no reader's
 * benefit. When the detail page grows something the list must not pay for, that is the day to
 * split it.
 *
 * WHO WORKS HERE IS NOT IN THIS FILE. The company page lists its people through
 * `GET /v1/contacts?companyId=…`, which already pages, already sorts and already applies the
 * `limited` reader's department scope. A second list endpoint would have been a second copy of all
 * three, and the third one would have been the one that forgot the scope.
 *
 * `contactCount` is the exception, and it earns its place: a firm with nobody at it is the thing
 * a reader wants to see from the list, without opening thirty pages to find it.
 */
import { z } from 'zod';

import { cursorQuerySchema, page } from '../http/pagination.js';
import { urlInputSchema } from '../url.js';

/**
 * The sorts `GET /v1/contact-companies` offers.
 *
 * * `name`    → `ix_contact_companies__name` (organization_id, lower(btrim(name)) COLLATE nb),
 *              partial on `deleted_at is null` — the same expression the keyset compares.
 * * `created` → `ux_contact_companies__org_id` (organization_id, id); ids are UUIDv7, so `id`
 *              **is** creation order and is its own tiebreaker.
 *
 * There is no `contacts` sort. Ordering by a counted lateral means counting every company in the
 * organization before the first row can be returned, which is the one thing keyset pagination
 * exists to avoid. A reader who wants the empty firms filters instead.
 */
export const CONTACT_COMPANY_SORT_KEYS = ['name', 'created'] as const;
export type ContactCompanySortKey = (typeof CONTACT_COMPANY_SORT_KEYS)[number];

const nameSchema = z.string().trim().min(1).max(200);
const shortTextSchema = z.string().trim().max(200);
const phoneSchema = z.string().trim().max(40);
const notesSchema = z.string().max(10_000);
const emailSchema = z.email().max(320);
/** Én normalisering for hele produktet — «godnokfestivalen.no» er en gyldig adresse (#16c). */
const urlSchema = urlInputSchema(500);

export const contactCompanyDtoSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    /** Organisasjonsnummer. Free text on purpose: a foreign agency has no Norwegian one. */
    organizationNumber: z.string().nullable(),
    websiteUrl: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    address: z.string().nullable(),
    notes: z.string().nullable(),
    /**
     * The counterparty in Tvitvi, when this firm is a house we are linked to (vedtak 99). It makes
     * «open God Nok in Kontakter» the same page whether or not they are a customer — the link is
     * what adds the shared history, not a different screen.
     */
    counterpartyOrganizationId: z.uuid().nullable(),
    /**
     * FORESPØRSELEN OM Å BLI KOBLET TIL DEN EKTE ORGEN (9a), når den finnes.
     *
     * `pending` betyr sendt og ubesvart, `accepted` at huset på den andre siden sa ja — og da er
     * navn, org.nr, adresse, telefon, e-post og nettside deres, ikke våre. Status er alt som står
     * her: hvem forespørselen gikk til, er det samme spørsmålet 8b forbyr oss å svare på.
     */
    link: z.object({ status: z.enum(['pending', 'accepted']) }).nullable(),
    /** How many live contacts point at this firm. Zero is a fact worth seeing from the list. */
    contactCount: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'ContactCompany' });

export const listContactCompaniesQuerySchema = cursorQuerySchema(CONTACT_COMPANY_SORT_KEYS)
  .extend({
    /** Prefix and substring, matched case-insensitively against the name alone. */
    search: z.string().trim().min(1).max(120).optional(),
  })
  .meta({ id: 'ListContactCompaniesQuery' });

export const contactCompanyListResponseSchema = page(
  contactCompanyDtoSchema,
  'ContactCompanyListResponse',
);

export const createContactCompanyRequestSchema = z
  .object({
    name: nameSchema,
    organizationNumber: shortTextSchema.nullish(),
    websiteUrl: urlSchema.nullish(),
    email: emailSchema.nullish(),
    phone: phoneSchema.nullish(),
    address: shortTextSchema.nullish(),
    notes: notesSchema.nullish(),
  })
  .meta({ id: 'CreateContactCompanyRequest' });

/**
 * `counterpartyOrganizationId` is deliberately absent, here and in create.
 *
 * Which organisation a firm IS is decided by accepting an invitation — `app.accept_organization_link`
 * writes it, having proved a token — and never by a client naming an organisation id. Letting this
 * endpoint set it would be a way to claim to be somebody else's house with one PATCH.
 */
export const updateContactCompanyRequestSchema = z
  .object({
    name: nameSchema.optional(),
    organizationNumber: shortTextSchema.nullish(),
    websiteUrl: urlSchema.nullish(),
    email: emailSchema.nullish(),
    phone: phoneSchema.nullish(),
    address: shortTextSchema.nullish(),
    notes: notesSchema.nullish(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateContactCompanyRequest' });

/**
 * «KOBLE TIL TVITVI-ORGANISASJON» (9a) — NUMMERET, IKKE EN ORG-ID.
 *
 * Klienten navngir aldri organisasjonen den vil koble til; den oppgir nummeret firmaet har på
 * brevet sitt, og serveren slår opp EKSAKT på sifrene. Å ta imot en org-id her ville vært den
 * samme feilen `updateContactCompanyRequestSchema` forklarer at den unngår: en måte å påstå at man
 * er noen andres hus med én forespørsel. Svaret er likt enten nummeret finnes i Tvitvi eller ikke
 * (8b), så ingen kan bruke skjemaet til å kartlegge hvem som er kunde her.
 */
export const requestCompanyLinkRequestSchema = z
  .object({ registrationNumber: z.string().trim().min(1).max(64) })
  .meta({ id: 'RequestCompanyLinkRequest' });

/**
 * HISTORIKKEN MELLOM DE TO HUSENE — «bud og forespørsler det året» (vedtak 97/99).
 *
 * ---------------------------------------------------------------------------------------------
 * DEN LESES FRA VÅRE EGNE RADER, og det er den viktigste setningen her.
 *
 * Antakelsen var at dette krevde et oppslag i motpartens base, altså en SECURITY DEFINER-funksjon
 * som krysser grensen. Det gjør det ikke: speilingen (vedtak 94.1) har allerede gitt HVERT hus sitt
 * eget eksemplar. En festival leser `booking_bids` — det de selv sendte, med byråets svar skrevet
 * på — og et byrå leser `booking_enquiries`, som er det de selv mottok. Ingen RLS-policy må vike,
 * og ingen ny funksjon trengs.
 *
 * `direction` sier hvilken av de to lesningene raden kom fra: `sent` er noe vi sendte, `received`
 * noe vi fikk. Det er ikke en kolonne i basen — det er hvilket bord den kom fra.
 */
export const COMPANY_HISTORY_DIRECTIONS = ['sent', 'received'] as const;
export type CompanyHistoryDirection = (typeof COMPANY_HISTORY_DIRECTIONS)[number];

export const companyHistoryEntryDtoSchema = z
  .object({
    id: z.uuid(),
    direction: z.enum(COMPANY_HISTORY_DIRECTIONS),
    /** `interest` eller `bid` — spørsmålet «er dere ledige» mot et faktisk beløp. */
    kind: z.string(),
    artistName: z.string(),
    amountMinor: z.number().int().nullable(),
    currency: z.string(),
    performanceDate: z.string().nullable(),
    /** «ledig», «hold», «opptatt» eller «ikke svart» — byråets svar, uansett hvem som leser. */
    availability: z.string(),
    holdUntil: z.string().nullable(),
    status: z.string(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'CompanyHistoryEntry' });

/**
 * Historikken pluss de bookede artistene, i ett svar.
 *
 * `bookedArtists` er navnene der noen faktisk sa ja: `availability = 'available'` er et svar, ikke
 * en booking, så lista leses fra de bekreftede programpostene der artisten hører til dette huset.
 * Er den tom, er det fordi ingenting er bekreftet ennå — og siden sier det framfor å skjule kortet.
 */
export const companyHistoryDtoSchema = z
  .object({
    companyId: z.uuid(),
    counterpartyOrganizationId: z.uuid().nullable(),
    /** Året historikken gjelder. Alltid satt: «det året» er spørsmålet Atle stilte. */
    year: z.number().int(),
    entries: z.array(companyHistoryEntryDtoSchema),
    bookedArtists: z.array(z.string()),
  })
  .meta({ id: 'CompanyHistory' });

export const companyHistoryQuerySchema = z
  .object({ year: z.coerce.number().int().min(1900).max(2200).optional() })
  .meta({ id: 'CompanyHistoryQuery' });

export type CompanyHistoryEntryDto = z.infer<typeof companyHistoryEntryDtoSchema>;
export type CompanyHistoryDto = z.infer<typeof companyHistoryDtoSchema>;
export type CompanyHistoryQuery = z.infer<typeof companyHistoryQuerySchema>;

export type ContactCompanyDto = z.infer<typeof contactCompanyDtoSchema>;
export type ListContactCompaniesQuery = z.infer<typeof listContactCompaniesQuerySchema>;
export type ContactCompanyListResponse = z.infer<typeof contactCompanyListResponseSchema>;
export type CreateContactCompanyRequest = z.infer<typeof createContactCompanyRequestSchema>;
export type UpdateContactCompanyRequest = z.infer<typeof updateContactCompanyRequestSchema>;
export type RequestCompanyLinkRequest = z.infer<typeof requestCompanyLinkRequestSchema>;
